import type { CSSProperties } from 'react';

/**
 * Official platform marks for Google Business Profile, Facebook and Instagram.
 *
 * The artwork ships on a white background (no alpha), so containers should be
 * white or neutral; the image itself carries a subtle rounded corner so it
 * reads as an app tile on any surface.
 */

export type PlatformIconKey =
  | 'google_business'
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'sendgrid'
  | 'twilio';

export const PLATFORM_ICON_SRC: Record<PlatformIconKey, string> = {
  google_business: '/platform-icons/google-business.png',
  facebook: '/platform-icons/facebook.png',
  instagram: '/platform-icons/instagram.png',
  linkedin: '/platform-icons/linkedin.svg',
  sendgrid: '/platform-icons/sendgrid.png',
  // Same asset the Integrations page has always used for Twilio.
  twilio: '/twilio.png',
};

export const PLATFORM_ICON_LABEL: Record<PlatformIconKey, string> = {
  google_business: 'Google Business Profile',
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  sendgrid: 'SendGrid',
  twilio: 'Twilio',
};

/** Map the many spellings used across the app onto one key. */
export function normalizePlatformKey(raw: unknown): PlatformIconKey | null {
  const t = String(raw || '').trim().toLowerCase().replace(/[\s_-]/g, '');
  if (!t) return null;
  if (['gmb', 'gbp', 'google', 'googlebusiness', 'googlebusinessprofile', 'googleprofile'].includes(t)) {
    return 'google_business';
  }
  if (t === 'facebook' || t === 'fb') return 'facebook';
  if (t === 'instagram' || t === 'ig') return 'instagram';
  if (t === 'linkedin' || t === 'li') return 'linkedin';
  if (t === 'sendgrid') return 'sendgrid';
  if (t === 'twilio') return 'twilio';
  return null;
}

interface PlatformIconProps {
  platform: PlatformIconKey | string;
  /** Pixel size when no width/height classes are supplied. */
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Square corners (e.g. inside an already-rounded container). */
  square?: boolean;
}

export default function PlatformIcon({
  platform,
  size,
  className = '',
  style,
  square = false,
}: PlatformIconProps) {
  const key = normalizePlatformKey(platform);
  if (!key) return null;
  const hasSizeClass = /\b(h-|w-|size-)/.test(className);
  return (
    <img
      src={PLATFORM_ICON_SRC[key]}
      alt={PLATFORM_ICON_LABEL[key]}
      width={hasSizeClass ? undefined : size ?? 20}
      height={hasSizeClass ? undefined : size ?? 20}
      draggable={false}
      decoding="async"
      className={`inline-block shrink-0 select-none object-contain bg-white ${
        square ? '' : 'rounded-[22%]'
      } ${className}`}
      style={style}
    />
  );
}

/**
 * Drop-in replacements for lucide icons in `Icon: ...` meta tables — accept
 * the same `className` / `style` props so call sites don't change.
 */
type LucideLikeProps = {
  className?: string;
  style?: CSSProperties;
  size?: number;
  /** Accepted for lucide compatibility; has no effect on an image. */
  strokeWidth?: number;
  square?: boolean;
};

export function GoogleBusinessIcon({ strokeWidth: _sw, ...props }: LucideLikeProps) {
  return <PlatformIcon platform="google_business" {...props} />;
}
export function FacebookIcon({ strokeWidth: _sw, ...props }: LucideLikeProps) {
  return <PlatformIcon platform="facebook" {...props} />;
}
export function InstagramIcon({ strokeWidth: _sw, ...props }: LucideLikeProps) {
  return <PlatformIcon platform="instagram" {...props} />;
}
export function LinkedInIcon({ strokeWidth: _sw, ...props }: LucideLikeProps) {
  return <PlatformIcon platform="linkedin" {...props} />;
}
export function SendgridIcon({ strokeWidth: _sw, ...props }: LucideLikeProps) {
  return <PlatformIcon platform="sendgrid" {...props} />;
}
export function TwilioIcon({ strokeWidth: _sw, ...props }: LucideLikeProps) {
  return <PlatformIcon platform="twilio" {...props} />;
}
